import Button from '@atlaskit/button';
import EditorEditIcon from '@atlaskit/icon/glyph/editor/edit';
import InlineEdit from '@atlaskit/inline-edit';
import Select, { OptionsType, ValueType } from '@atlaskit/select';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { PageLayer, Permissions, Roles } from '@atlassian/cssxp-typescommon';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';
import upperFirst from 'lodash/upperFirst';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { errorNotificationBuilder, successNotificationBuilder } from '../../notifications/utils/notification-builder';
import { addNotification } from '../../state/src/notifications-slice';
import { changeRenewalAction } from '@atlassian/temp-proxies';
import { ENABLE_CASSI_PAGES_MIGRATION } from '@atlassian/cssxp-utils-generic';
import { useFeatureToggle } from '../../hooks';
import { hasAtleastOneRole, isAccess } from '../../security';
export interface Props {
  billingFrequency?: string;
  renewalAction?: string;
  accountId: string;
  isEntitlementActive?: boolean;
}

interface ChangeRenewalOptionType {
  label: string;
  value: string;
}

const ChangeRenewalField = (props: Props) => {
  const { renewalAction, accountId, billingFrequency, isEntitlementActive } = props;
  const canChangeRenewalAction =
    isAccess(Permissions.CLOUD_SITE_MANAGE) &&
    hasAtleastOneRole([
      Roles.CUSTOMER_ADVOCATE,
      Roles.LOYALTY_ADVOCATE,
      Roles.PRODUCT_ADVOCATE,
      Roles.MKTG_GTM_SUPPORT,
    ]);
  const [isEnabled] = useFeatureToggle();
  const [isProcessing, setIsProcessing] = useState(false);
  const [editValue, setEditValue] = useState(renewalAction);
  const dispatch = useDispatch();

  useEffect(() => {
    const updateValue = async () => {
      let notification;
      if (isEmpty(editValue)) {
        return;
      }
      try {
        setIsProcessing(true);
        // Evaluations have a null renewal action; therefore if set somehow, error early
        if (editValue === undefined) {
          throw new Error('Renewal action must be defined');
        } else {
          // const requestPayload = {
          //   renewalAction: editValue,
          // };
          //await billingApiGateway.changeRenewalAction(accountId, requestPayload);     //Todo - Implement API as part of seperate user story
          const requestPayload = {
            renewalAction: editValue,
          };
          //await billingApiGateway.changeRenewalAction(accountId, requestPayload);
          //Todo - Implement API as part of seperate user story
          await changeRenewalAction(accountId, requestPayload);
          //await Promise.resolve(); // Remove this mock once API is implemented
          notification = successNotificationBuilder().withHeader('Renewal action updated successfully').build();
          dispatch(addNotification(notification));
        }
      } catch (e: any) {
        setEditValue(renewalAction);
        notification = errorNotificationBuilder()
          .withHeader('Error')
          .withMessage(`<b>Renewal action update failed. </b>${e.message}`)
          .build();
        dispatch(addNotification(notification));
      } finally {
        setIsProcessing(false);
      }
    };

    if (renewalAction !== editValue) {
      updateValue();
    }
  }, [dispatch, editValue, accountId, renewalAction]);

  const getRenewalOptions = (): OptionsType<ChangeRenewalOptionType> => {
    const options = [
      { label: 'Auto-renew', value: 'AUTO_RENEW' },
      { label: 'None', value: 'NONE' },
    ];

    if (billingFrequency !== 'MONTHLY') {
      options.unshift({ label: 'Auto-quote', value: 'AUTO_QUOTE' });
    }

    return options;
  };

  const getReadOnlyMessage = () => {
    const showNotApplicable = isEmpty(editValue);

    if (showNotApplicable || !isEntitlementActive) {
      return <p className="data-unavailable">N/A</p>;
    }

    return upperFirst(kebabCase(`${editValue}`));
  };

  if (isProcessing === true) {
    return <LoadingIndicator data-test-id="loading-indicator" />;
  }

  const options = getRenewalOptions();
  const defaultValue = options.find((option) => option.value === editValue) as ChangeRenewalOptionType;

  const canEditRenewalAction =
    // if the feature flag / permissions allows edit
    canChangeRenewalAction === true &&
    // if a value is currently set
    !isEmpty(editValue) &&
    // Some staging data do not have an account id set, make read only
    !!accountId &&
    // There is an OTHER billingFrequency
    (billingFrequency === 'MONTHLY' || billingFrequency === 'ANNUAL');

  return (
    <div data-testid="renewal-inline-edit">
      <label className="change-renewal">Renewal Action</label>
      {canEditRenewalAction ? (
        <InlineEdit<ValueType<ChangeRenewalOptionType>>
          defaultValue={defaultValue}
          editView={(fieldProps) => {
            return (
              <div
                style={{
                  margin: '0 auto',
                  zIndex: PageLayer.INLINE_EDIT,
                  position: 'relative',
                  textAlign: 'left',
                }}
              >
                <Select<ChangeRenewalOptionType>
                  {...fieldProps}
                  isDisabled={!isEnabled(ENABLE_CASSI_PAGES_MIGRATION)}
                  options={options}
                  autoFocus
                  openMenuOnFocus
                  isSearchable={false}
                  styles={{
                    control: (base: Record<string, unknown>) => ({
                      ...base,
                      padding: 0,
                      border: 0,
                      minHeight: 'unset',
                      background: 'unset',
                      fontWeight: 400,
                      textAlign: 'center',
                      justifyContent: 'center',
                      ':hover': {
                        background: 'unset',
                      },
                    }),
                    container: (base: Record<string, unknown>) => ({
                      ...base,
                      width: 160,
                      margin: '0 auto',
                      padding: 0,
                      height: '20px',
                    }),
                    menu: (base: Record<string, unknown>) => ({
                      ...base,
                      marginTop: '12px',
                    }),
                  }}
                />
              </div>
            );
          }}
          readView={() => (
            <div>
              <Button
                iconAfter={<EditorEditIcon label="edit" />} //ITFO-4417
                style={{
                  lineHeight: 1,
                  height: '24px',
                  width: '160px',
                  background: 'transparent',
                  justifyContent: 'center',
                  fontWeight: 'normal',
                }}
              >
                {upperFirst(kebabCase(editValue))}
              </Button>
            </div>
          )}
          onConfirm={(event) => {
            if (!isEmpty(event.value)) {
              setEditValue(event.value);
            }
          }}
        />
      ) : (
        <div>
          {/* Replicating button styles from above to keep ux consistency */}
          <Button
            isDisabled={true}
            css={{
              lineHeight: 1,
              height: '20px',
              width: '160px',
              background: 'transparent',
              justifyContent: 'center',
              cursor: 'default !important',
              color: 'inherit !important',
            }}
          >
            {getReadOnlyMessage()}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChangeRenewalField;
